/**
 * Choice manager
 * Handles rendering and interaction of choice scenes
 */

import { gsap } from 'gsap'
import { SaveStateManager } from '../../../features/save-state'
import { getAudioManager } from '../../../features/audio'
import {
  getChoiceImagePath,
  getChoiceBgPath,
  getChoiceBorderPath,
  getChoiceBorderActivePath,
  applyResponsiveImage,
} from '../../../utils/pathResolver'
import type { ChoiceContext } from './types'
import type { ChoiceCondition, ChoiceEffect } from '../types'
import graphicCheckSvg from '../../../svg/graphic-check.svg?raw'

/**
 * Resolve choice result to a scene ID
 * Handles conditional results and direct scene IDs
 */
export function resolveChoiceResult(
  result: string | { sceneId: string; when?: ChoiceCondition } | Array<{ sceneId: string; when?: ChoiceCondition }>,
  onEvaluateCondition?: (condition: ChoiceCondition) => boolean,
): string | null {
  // Simple string - direct scene ID
  if (typeof result === 'string') {
    return result
  }

  // Object with optional condition
  if (!Array.isArray(result)) {
    if (result.when && onEvaluateCondition) {
      // Check condition
      if (onEvaluateCondition(result.when)) {
        return result.sceneId
      }
      // Condition not met - no default, return null
      return null
    }
    // No condition - use sceneId
    return result.sceneId
  }

  // Array of conditional results - find first matching condition
  // Items without 'when' act as default fallback
  if (onEvaluateCondition) {
    for (const item of result) {
      // If no condition specified, use as default fallback
      if (!item.when) {
        return item.sceneId
      }
      // Check condition
      if (onEvaluateCondition(item.when)) {
        return item.sceneId
      }
    }
  } else {
    // If no evaluator, return first item without condition, or first item
    for (const item of result) {
      if (!item.when) {
        return item.sceneId
      }
    }
    // If all have conditions but no evaluator, return first as fallback
    if (result.length > 0) {
      return result[0].sceneId
    }
  }

  // No conditions matched and no default
  return null
}

/**
 * Save choice to state
 */
export function saveChoice(sceneId: string, choiceNumber: number): void {
  // Load current state to preserve all other data
  const savedState = SaveStateManager.load() || SaveStateManager.createInitialState()
  if (!savedState.choices) {
    savedState.choices = {}
  }
  savedState.choices[sceneId] = choiceNumber

  // Save immediately and synchronously to ensure choice is persisted
  // This is critical data that must be saved before any page refresh
  SaveStateManager.saveImmediate(savedState)
}

/**
 * Create a choice bubble button
 */
export function createChoiceBubble(
  imagePath: string,
  choiceNumber: number,
  choiceText: string,
  skipAnimation: boolean = false,
  onClick: (choiceNumber: number) => void,
): HTMLButtonElement {
  const button = document.createElement('button')
  button.className = `choice-button choice-${choiceNumber}`
  button.type = 'button'
  button.setAttribute('aria-label', choiceText)

  // Create container div (replaces choice-bubble img)
  const choiceContainer = document.createElement('div')
  choiceContainer.className = 'choice-bubble'
  choiceContainer.style.position = 'relative'

  // Background image (defines dimensions, not absolutely positioned)
  const bgImg = document.createElement('img')
  bgImg.src = getChoiceBgPath(choiceNumber)
  bgImg.className = 'choice-bg'
  bgImg.alt = ''
  bgImg.style.display = 'block'
  bgImg.style.width = '100%'
  bgImg.style.height = 'auto'

  // Border image (absolutely positioned)
  const borderImg = document.createElement('img')
  borderImg.src = getChoiceBorderPath(choiceNumber)
  borderImg.className = 'choice-border'
  borderImg.alt = ''
  borderImg.style.position = 'absolute'
  borderImg.style.top = '0'
  borderImg.style.left = '0'
  borderImg.style.width = '100%'
  borderImg.style.height = '100%'
  borderImg.style.objectFit = 'cover'

  // Active border image (absolutely positioned, hidden by default)
  const borderActiveImg = document.createElement('img')
  borderActiveImg.src = getChoiceBorderActivePath(choiceNumber)
  borderActiveImg.className = 'choice-border-active'
  borderActiveImg.alt = ''
  borderActiveImg.style.position = 'absolute'
  borderActiveImg.style.top = '0'
  borderActiveImg.style.left = '0'
  borderActiveImg.style.width = '100%'
  borderActiveImg.style.height = '100%'
  borderActiveImg.style.objectFit = 'cover'
  borderActiveImg.style.display = 'none'

  // Choice content image (absolutely positioned)
  const choiceImg = document.createElement('img')
  choiceImg.src = imagePath
  choiceImg.className = 'choice-content'
  choiceImg.alt = choiceText
  choiceImg.style.position = 'absolute'
  choiceImg.style.top = '0'
  choiceImg.style.left = '0'
  choiceImg.style.width = '100%'
  choiceImg.style.height = '100%'
  choiceImg.style.objectFit = 'cover'

  applyResponsiveImage(bgImg)
  applyResponsiveImage(borderImg)
  applyResponsiveImage(borderActiveImg)
  applyResponsiveImage(choiceImg)

  choiceContainer.appendChild(bgImg)
  choiceContainer.appendChild(borderImg)
  choiceContainer.appendChild(borderActiveImg)
  choiceContainer.appendChild(choiceImg)

  button.appendChild(choiceContainer)

  // Add click handler
  button.addEventListener('click', () => {
    onClick(choiceNumber)
  })

  if (skipAnimation) {
    // Show immediately when restoring
    choiceContainer.style.opacity = '1'
    choiceContainer.style.transform = 'scale(0.95)'
  } else {
    // Animate in
    choiceContainer.style.opacity = '0'
    choiceContainer.style.transform = 'scale(0.8)'
    requestAnimationFrame(() => {
      choiceContainer.style.opacity = '1'
      choiceContainer.style.transform = 'scale(0.95)'
    })
  }

  return button
}

/**
 * Show choices container
 */
export function showChoices(
  context: ChoiceContext,
  onChoiceClick: (choiceNumber: number) => void,
  choiceButtons: HTMLButtonElement[],
): number | undefined {
  if (!context.data.choices) return undefined

  const savedChoice = SaveStateManager.load()?.choices?.[context.data.id]
  const isRestoring = !!savedChoice
  const choicesContainer = buildChoicesContainer(context, onChoiceClick, choiceButtons, isRestoring)
  context.container.appendChild(choicesContainer)
  return savedChoice ?? undefined
}

function buildChoicesContainer(
  context: ChoiceContext,
  onChoiceClick: (choiceNumber: number) => void,
  choiceButtons: HTMLButtonElement[],
  isRestoring: boolean,
): HTMLDivElement {
  const container = document.createElement('div')
  container.className = 'choices-container'
  const choice1Text = context.data.choices!.choice1.text || 'Choice 1'
  const choice2Text = context.data.choices!.choice2.text || 'Choice 2'
  const choice1 = createChoiceBubble(
    getChoiceImagePath(context.data.id, 1),
    1,
    choice1Text,
    isRestoring,
    onChoiceClick,
  )
  const choice2 = createChoiceBubble(
    getChoiceImagePath(context.data.id, 2),
    2,
    choice2Text,
    isRestoring,
    onChoiceClick,
  )
  container.appendChild(choice1)
  container.appendChild(choice2)
  choiceButtons.push(choice1, choice2)
  return container
}

/**
 * Handle choice selection (without confirmation)
 * Plays sound and updates visual state
 */
export function handleChoiceSelection(
  choiceNumber: number,
  context: ChoiceContext,
  choiceButtons: HTMLButtonElement[],
): void {
  getAudioManager().playChoiceSound(choiceNumber, context.data.id)
  updateChoiceScales(context, choiceNumber, choiceButtons)
}

/**
 * Handle choice confirmation (when user clicks confirm button)
 */
export function handleChoiceClick(
  choiceNumber: number,
  context: ChoiceContext,
  choiceButtons: HTMLButtonElement[],
): string | null {
  saveChoice(context.data.id, choiceNumber)
  disableChoiceButtons(context)

  const choiceConfig = getChoiceConfig(context, choiceNumber)
  const sceneId = resolveChoiceResult(choiceConfig.result, context.onEvaluateCondition)

  // Apply effects after resolving the result (effects like removeInventory
  // must not interfere with condition checks in the result resolution)
  if (choiceConfig.effects) {
    applyChoiceEffects(choiceConfig.effects)
  }

  if (!sceneId) {
    console.error(`No valid scene result found for choice ${choiceNumber}`)
    return null
  }

  animateChoiceSelection(context, choiceNumber, sceneId)
  return sceneId
}

/**
 * Update choice scales: selected = 1.05, unselected = 0.95
 * Removes animations from both choices when any choice is clicked
 * Toggles border images based on selection state
 */
function updateChoiceScales(
  context: ChoiceContext,
  selectedChoiceNumber: number,
  choiceButtons: HTMLButtonElement[],
): void {
  choiceButtons.forEach((button) => {
    const choiceContainer = button.querySelector('.choice-bubble') as HTMLElement | null
    if (!choiceContainer) return

    const buttonChoiceNumber = button.classList.contains('choice-1') ? 1 : 2
    const isSelected = buttonChoiceNumber === selectedChoiceNumber

    // Remove CSS animation from both choices when any choice is clicked
    button.style.animation = 'none'

    // Toggle border images
    const borderImg = button.querySelector('.choice-border') as HTMLImageElement | null
    const borderActiveImg = button.querySelector('.choice-border-active') as HTMLImageElement | null

    if (borderImg && borderActiveImg) {
      if (isSelected) {
        borderImg.style.display = 'none'
        borderActiveImg.style.display = 'block'
      } else {
        borderImg.style.display = 'block'
        borderActiveImg.style.display = 'none'
      }
    }

    // Update scale
    gsap.to(choiceContainer, {
      scale: isSelected ? 1.05 : 0.95,
      duration: 0.2,
      ease: 'power2.out',
    })
  })
}

function applyChoiceEffects(effects: ChoiceEffect[]): void {
  const saved = SaveStateManager.load() ?? SaveStateManager.createInitialState()
  let changed = false

  for (const effect of effects) {
    if (effect.type === 'removeInventory') {
      const hadItem = saved.inventory?.includes(effect.item)
      if (hadItem) {
        saved.inventory = saved.inventory!.filter((item) => item !== effect.item)
        if (!saved.consumedInventory) saved.consumedInventory = []
        if (!saved.consumedInventory.includes(effect.item)) {
          saved.consumedInventory.push(effect.item)
        }
        changed = true
      }
    }
  }

  if (changed) {
    SaveStateManager.saveImmediate(saved)
  }
}

function disableChoiceButtons(context: ChoiceContext): void {
  const btn1 = context.container.querySelector('.choice-1') as HTMLButtonElement | null
  const btn2 = context.container.querySelector('.choice-2') as HTMLButtonElement | null
  if (btn1) btn1.disabled = true
  if (btn2) btn2.disabled = true
}

function getChoiceConfig(context: ChoiceContext, choiceNumber: number) {
  return choiceNumber === 1 ? context.data.choices!.choice1 : context.data.choices!.choice2
}

function animateChoiceSelection(context: ChoiceContext, choiceNumber: number, _sceneId: string): void {
  const otherNumber = choiceNumber === 1 ? 2 : 1
  const otherButton = context.container.querySelector(`.choice-${otherNumber}`) as HTMLElement | null
  const clickedButton = context.container.querySelector(`.choice-${choiceNumber}`) as HTMLElement | null
  const choicesContainer = context.container.querySelector('.choices-container') as HTMLElement | null
  const fadeDuration = context.config.choices?.fadeDuration ?? 0.4
  const expandDuration = context.config.choices?.expandDuration ?? 0.5

  if (!otherButton) return

  gsap.killTweensOf(otherButton)
  otherButton.style.transition = 'none'
  const currentOpacity = gsap.getProperty(otherButton, 'opacity') as number

  gsap.to(otherButton, {
    opacity: 0,
    duration: fadeDuration,
    ease: 'power2.out',
    force3D: true,
    onStart: () => {
      gsap.set(otherButton, { opacity: currentOpacity })
    },
    onComplete: () => {
      otherButton.style.visibility = 'hidden'
      otherButton.setAttribute('aria-hidden', 'true')
      otherButton.style.pointerEvents = 'none'
      gsap.set(otherButton, { opacity: 0, clearProps: 'transform' })
      if (choicesContainer && clickedButton) {
        gsap.to(otherButton, { flex: '0 0 0%', minWidth: 0, duration: expandDuration, ease: 'power2.out' })
        gsap.to(clickedButton, { flex: '0 1 50%', minWidth: 0, duration: expandDuration, ease: 'power2.out' })
      }
    },
  })
}

/**
 * Restore choice state (when loading saved game)
 */
export function restoreChoiceState(choiceNumber: number, context: ChoiceContext): string | null {
  ensureChoiceSavedInState(context.data.id, choiceNumber)
  disableChoiceButtons(context)
  applyRestoredChoiceLayout(context, choiceNumber)

  const choiceConfig = getChoiceConfig(context, choiceNumber)
  const sceneId = resolveChoiceResult(choiceConfig.result, context.onEvaluateCondition)
  if (choiceConfig.effects) {
    applyChoiceEffects(choiceConfig.effects)
  }
  return sceneId
}

function ensureChoiceSavedInState(sceneId: string, choiceNumber: number): void {
  const currentState = SaveStateManager.load()
  if (!currentState || currentState.choices?.[sceneId] === choiceNumber) return
  if (!currentState.choices) currentState.choices = {}
  currentState.choices[sceneId] = choiceNumber
  SaveStateManager.saveImmediate(currentState)
}

function applyRestoredChoiceLayout(context: ChoiceContext, choiceNumber: number): void {
  const otherNumber = choiceNumber === 1 ? 2 : 1
  const otherButton = context.container.querySelector(`.choice-${otherNumber}`) as HTMLElement | null
  const selectedButton = context.container.querySelector(`.choice-${choiceNumber}`) as HTMLElement | null
  if (otherButton) {
    otherButton.style.opacity = '0'
    otherButton.style.visibility = 'hidden'
    otherButton.setAttribute('aria-hidden', 'true')
    otherButton.style.pointerEvents = 'none'
    otherButton.style.flex = '0 0 0%'
    otherButton.style.minWidth = '0'
  }
  if (selectedButton) {
    selectedButton.style.flex = '0 1 50%'
    selectedButton.style.minWidth = '0'
  }
}

/**
 * Create confirmation button element (reusable helper)
 */
export function createConfirmationButtonElement(onConfirm: () => void, label: string = 'Vybrat'): HTMLButtonElement {
  const button = document.createElement('button')
  button.className = 'button button--graphic-check'
  button.type = 'button'
  button.textContent = label

  const graphicDiv = document.createElement('div')
  graphicDiv.className = 'graphic'
  graphicDiv.innerHTML = graphicCheckSvg

  button.appendChild(graphicDiv)

  // Add click handler
  button.addEventListener('click', () => {
    onConfirm()
  })

  return button
}

/**
 * Create and show confirmation button (for choices)
 */
export function createConfirmationButton(
  context: ChoiceContext,
  onConfirm: () => void,
  onAnimationComplete?: () => void,
): HTMLButtonElement {
  // Remove existing button if any
  const existingButton = context.container.querySelector('.button--graphic-check') as HTMLButtonElement | null
  if (existingButton) {
    existingButton.remove()
  }

  // Get button label from choices config or use default
  const buttonLabel = context.data.choices?.buttonLabel || 'Vybrat'
  const button = createConfirmationButtonElement(onConfirm, buttonLabel)

  // Find choices container and append button below it
  const choicesContainer = context.container.querySelector('.choices-container')
  if (choicesContainer && choicesContainer.parentNode) {
    choicesContainer.parentNode.insertBefore(button, choicesContainer.nextSibling)
  } else {
    context.container.appendChild(button)
  }

  // Animate button in (fade in only, no movement)
  gsap.set(button, { opacity: 0 })
  gsap.to(button, {
    opacity: 1,
    duration: 0.3,
    ease: 'power2.out',
    onComplete: () => {
      if (onAnimationComplete) {
        onAnimationComplete()
      }
    },
  })

  return button
}

/**
 * Remove confirmation button (reusable helper)
 */
export function removeConfirmationButtonElement(button: HTMLButtonElement): void {
  gsap.to(button, {
    opacity: 0,
    duration: 0.2,
    ease: 'power2.in',
    onComplete: () => {
      button.remove()
    },
  })
}

/**
 * Remove confirmation button (for choices)
 */
export function removeConfirmationButton(context: ChoiceContext): void {
  const button = context.container.querySelector('.button--graphic-check') as HTMLButtonElement | null
  if (button) {
    removeConfirmationButtonElement(button)
  }
}
